// Slip readiness gate.
//
// A run's incremental timing data (splits, finals, MOV, over/under) should only
// be shown once the run is actually finished. We treat a slip as "ready" when
// either:
//   - a win is available on the run (a lane row has win === "WIN"), or
//   - no win yet, but the run is at least MIN_AGE_MS old.
//
// The age is measured from the run's own timestamp — the raceUniqueId stamp the
// timing system emits, e.g. "05-22-2026_15-57-20-913322_EDT-0400"
// (MM-DD-YYYY_HH-MM-SS-micros_<TZ><±offset>). The trailing UTC offset is used so
// the age is correct regardless of the viewer's local timezone.

export const MIN_AGE_MS = 5 * 60 * 1000; // 5 minutes

// Parse a raceUniqueId stamp to an absolute epoch-ms instant, honoring the
// embedded UTC offset. Returns null when the value is missing/unparseable so
// callers can fail open (show data) rather than hide it forever.
export const parseRaceInstant = (raceUniqueId) => {
  if (raceUniqueId == null) return null;
  const s = String(raceUniqueId).trim();
  if (!s || s === "NA" || s === "-") return null;

  // MM-DD-YYYY _ HH-MM-SS -micros _ <TZABBR><±HHMM>
  const m = s.match(
    /^(\d{2})-(\d{2})-(\d{4})_(\d{2})-(\d{2})-(\d{2})(?:-(\d{1,6}))?_[A-Za-z]+([+-]\d{4})$/,
  );
  if (!m) return null;

  const [, mm, dd, yyyy, hh, min, ss, micros, offset] = m;
  const ms = micros ? String(micros).slice(0, 3).padEnd(3, "0") : "000";
  // Assemble an ISO-8601 string with an explicit offset (e.g. -04:00) so Date
  // resolves it to a fixed instant independent of the browser timezone.
  const iso = `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}.${ms}${offset.slice(0, 3)}:${offset.slice(3)}`;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

// Whether any lane on the run has been declared the winner. The timing system
// sets win to the literal "WIN" on the winning lane at the moment the race is
// decided (the losing lane stays null/"NA").
export const hasWin = (results) =>
  Array.isArray(results) && results.some((r) => r && r.win === "WIN");

// The display gate. `results` is the array rendered by the Slip view.
export const isSlipReady = (results, opts = {}) => {
  const { nowMs = Date.now(), minAgeMs = MIN_AGE_MS } = opts;

  if (hasWin(results)) return true;

  const stamp = Array.isArray(results) ? results[0]?.raceUniqueId : null;
  const t = parseRaceInstant(stamp);
  if (t == null) return true; // fail open — never hide valid data indefinitely

  return nowMs - t >= minAgeMs;
};
